import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Alert, Link, Tooltip, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { FirebaseError } from "firebase/app";
import { signInWithCustomToken, signInWithPhoneNumber } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { formatLastFour } from "src/utils/mrr/formatLastFour";
import * as Yup from 'yup';
import { AUTH, CLOUD_FUNCTIONS } from "../../auth/FirebaseContext";
import { SignInMethod, useLoginFlow } from "../../auth/LoginProvider";
import { brandConfig } from "../../config";
import { cloud_confirmOneTimePassword, cloud_emailOneTimePassword, cloud_updateUserBrand } from "../../utils/mrr/cloudFunctions";
import { RHFCodes } from "../hook-form";
import FormProvider from "../hook-form/FormProvider";


type ConfirmCodeProps = {
    back(): void,
}

const VerifyCodeSchema = Yup.object().shape({
    code1: Yup.string().required('Code is required'),
    code2: Yup.string().required('Code is required'),
    code3: Yup.string().required('Code is required'),
    code4: Yup.string().required('Code is required'),
    code5: Yup.string().required('Code is required'),
    code6: Yup.string().required('Code is required'),
});

const defaultValues = {
    code1: '',
    code2: '',
    code3: '',
    code4: '',
    code5: '',
    code6: '',
};
const ConfirmCode = ({ back }: ConfirmCodeProps) => {
    const { emailToVerify, signInMethod, phoneToVerify, confirmationResult, setConfirmationResult, recaptcha } = useLoginFlow();
    const rhfMethods = useForm({ defaultValues, resolver: yupResolver(VerifyCodeSchema), mode: 'onChange' })
    const { handleSubmit, formState: { isValid, isSubmitting, isSubmitted }, trigger } = rhfMethods;
    const [error, setError] = useState<string | null>(null);
    const [navigating, setNavigating] = useState(false);
    const [codeResent, setCodeResent] = useState(false);
    const [resendError, setResendError] = useState<string | null>(null);
    const [resendingLink, setResendingLink] = useState(false);
    const submitRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (isValid) {
            submitRef.current?.click()
        }
    }, [isValid])

    const onSubmit = handleSubmit(async (data) => {
        setError(null)
        const code = Object.values(data).join('')
        if (signInMethod === SignInMethod.Email) {
            try {
                const request = httpsCallable<{ email: string, code: string, brand: string }, { customToken: string, message: string, success: boolean }>(CLOUD_FUNCTIONS, cloud_confirmOneTimePassword);
                const { success, customToken } = (await request({ email: emailToVerify, code, brand: brandConfig.brandCode })).data;
                if (!success || !customToken) {
                    setError('The entered 6-digit code is invalid. Please try again.');
                    return;
                }
                const emailBrandRequest = httpsCallable<{ email: string, brand: string }, boolean>(CLOUD_FUNCTIONS, cloud_updateUserBrand);
                await emailBrandRequest({ email: emailToVerify, brand: brandConfig.brandCode });
                await signInWithCustomToken(AUTH, customToken).then(() => {
                    //NOTE: This case is cosmetic. Auth has already happened, and the guards are loading the page.

                    // Keep the spinner active on successful sign in.
                    setNavigating(true);
                })
            } catch (e) {
                setError('Please try again.');
            }
            return;
        }
        if (signInMethod === SignInMethod.Phone) {
            if (!confirmationResult) {
                back();
                return;
            };
            try {
                await confirmationResult.confirm(code).then(() => {
                    // Keep the spinner active on successful sign in.
                    setNavigating(true);
                });
            } catch (e) {
                console.warn(e);
                setError('The entered 6-digit code is invalid. Please try again.')
            }
        }
    })

    if (!signInMethod || (signInMethod === SignInMethod.Phone && !confirmationResult)) {
        back();
        return null;
    }

    const handleResendCode = async () => {
        setResendingLink(true);
        setCodeResent(false);
        setResendError(null);
        if (signInMethod === SignInMethod.Email) {
            const request = httpsCallable<{ email: string, brand: string }, boolean>(CLOUD_FUNCTIONS, cloud_emailOneTimePassword);
            const sentSignInEmailCode = await request({ email: emailToVerify, brand: brandConfig.brandCode });
            if (!sentSignInEmailCode) {
                setResendError('Sorry we could not email you a code. Please try again.')
                setResendingLink(false);
                return;
            }
            setResendingLink(false);
            setCodeResent(true);
            return
        }
        if (signInMethod === SignInMethod.Phone && recaptcha) {
            await signInWithPhoneNumber(AUTH, phoneToVerify, recaptcha).then(async (nextPayload) => {
                const resendPhoneBrandRequest = httpsCallable<{ email: string, brand: string }, boolean>(CLOUD_FUNCTIONS, cloud_updateUserBrand);
                await resendPhoneBrandRequest({ email: emailToVerify, brand: brandConfig.brandCode });
                setConfirmationResult(nextPayload);
                setResendingLink(false);
                setCodeResent(true);
            }).catch((e) => {
                setResendingLink(false);
                if (e instanceof FirebaseError && e.code === 'auth/too-many-requests') {
                    setResendError('Too many attempts. Please try again later.')
                    return;
                };
                setResendError('Sorry we could not send you a code. Please try again.')
            })

        }
    };

    const alertText = signInMethod === SignInMethod.Email
        ? "A 6-digit code has been sent to your email. Please enter it here to login."
        : `A 6-digit code has been sent to your phone ending in ${formatLastFour(phoneToVerify)}. Please enter it here to login.`

    const loading = isSubmitting || navigating;
    return (
        <Stack spacing={2} direction='column'>
            <Alert severity="info">
                {alertText}
            </Alert>
            <FormProvider methods={rhfMethods} onSubmit={onSubmit} id='login-code-form'>
                <RHFCodes onPaste={(_inputs) => {
                    trigger();
                }} disabled={loading} placeHolder='' fullWidth keyName="code" inputs={['code1', 'code2', 'code3', 'code4', 'code5', 'code6']} onInput={() => { setCodeResent(false); setError(null); setResendError(null) }} />
                {isValid && error && <Typography variant='caption' color='error'>{error}</Typography>}
                {!isValid && isSubmitted && <Typography variant='caption' color='error'>Please provide a valid 6-digit code.</Typography>}
            </FormProvider>
            <LoadingButton loading={loading} disabled={!isValid || loading} ref={submitRef} type='submit' variant='contained' form='login-code-form'>Continue</LoadingButton>
            {signInMethod &&
                <Stack direction='column' alignItems='center'>
                    <Typography component='span'>
                        Didn&apos;t get a code?&nbsp;
                        <Tooltip title={`Resend code to ${signInMethod}.`}>
                            <Link
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (loading) {
                                        return;
                                    }
                                    await handleResendCode();
                                }}
                                sx={{ width: 'fit-content', cursor: 'pointer', ...((resendingLink || loading) && { color: 'text.disabled' }) }}>
                                Resend
                            </Link>
                        </Tooltip>
                    </Typography>
                    {resendError && <Typography variant='caption' color='error'>{resendError}</Typography>}
                    {codeResent && <Typography variant='caption' color='text-secondary'>Code resent</Typography>}
                </Stack>
            }
            <Link onClick={back} sx={{ width: 'fit-content', cursor: 'pointer' }}>Back</Link>
        </Stack>
    )
};

export default ConfirmCode;